import { Form } from "@client/content/controls";
import { Media as MediaComponent } from "@client/content/controls/Media/component";
import { getRootContainerProps, getRootContainerPropsDep } from "@client/content/lib/helper";
import { ModuleButton } from "@client/content/modules/Button";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { Alert, ShowMoreTextAlert } from "@client/controls/Alert/component";
import Badge from "@client/controls/Badge/component";
import { type FormFieldInfo } from "@client/controls/Form/component";
import Select from "@client/controls/Inputs/Select/component";
import TextArea from "@client/controls/Inputs/TextArea/component";
import TextInput from "@client/controls/Inputs/TextInput/component";
import { ShowMoreList } from "@client/controls/List/ShowMoreList/component";
import { FormModal } from "@client/controls/Modal/Modals";
import { type FormModalButton, type FormModalButtonProps } from "@client/controls/Modal/Modals/Form/component";
import { sort } from "@client/lib/array";
import { formatToHtml } from "@client/lib/format";
import { usePromise } from "@client/lib/hooks";
import { type UploadEndpointInfo } from "@common/components/Media/Upload/types";
import Accordion, { AutoExpandItems } from "@common/controls/Accordion";
import { type Primitive, SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import getOptions from "@common/lib/options";
import { getMaxLengthValidator } from "@common/lib/validate";
import { mapFormFieldsWithContextToDynamicFields } from "@common/modules/system/lib";
import * as React from "react";
import api from "../api";
import { type MemberHelpdesk } from "../types";

export type MapProps = RootContainerProps & {
  apiInfo: ModuleApiInfo;
  canCreateTicket: boolean;
  error: string | null;
  guidelines: MemberHelpdesk.Api.Guideline[] | null;
  guidelinesVideoUrl: string | null;
  displayHelpdeskGuidelines: boolean;
  isVisible: boolean;
  status: MemberHelpdesk.Status;
  uploadApiInfo: UploadEndpointInfo | null;
  violations: MemberHelpdesk.Api.GuidelineViolation[] | null;
};

export type ActionProps = {
  onClose: () => void;
  onSubmit: (data: MemberHelpdesk.View.NewTicket, customFieldIds: string[]) => void;
  onCloseViolation: (data: string) => void;
  onAgree: () => void;
};

export type Props = ActionProps & MapProps;
export default (props: Props) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const [departmentId, setDepartmentId] = React.useState<Primitive | undefined>();
  const [departments, isLoadingDepartments] = usePromise({
    fn: options.getOptions,
    args: ["helpdesk", "departments", { cultureCode: props.apiInfo.baseParams.cultureCode }],
    shouldLoad: props.isVisible
  });
  const [ticketTypes] = usePromise({
    fn: options.getOptions<string>,
    args: ["helpdesk", "ticketTypes", { departmentId, cultureCode: props.apiInfo.baseParams.cultureCode }],
    shouldLoad: !!departmentId && props.isVisible
  });
  const [customFields] = usePromise({
    fn: api.getCustomFields,
    args: [props.apiInfo, departmentId],
    shouldLoad: props.isVisible
  });
  // TODO: should be null by default or inherit from props
  const [step, setStep] = React.useState<"video" | "guideline" | null>("video");

  const fields = React.useMemo(() => {
    let fields: FormFieldInfo[] = [
      {
        name: "subject",
        label: i18n.subject,
        isRequired: true,
        defaultValue: "",
        Component: TextInput,
        validation: getMaxLengthValidator(128, i18n.infoSubjectTooLong)
      },
      {
        name: "message",
        label: i18n.message,
        isRequired: true,
        Component: TextArea,
        componentOpts: { rows: 5 }
      }
    ];

    if (ticketTypes && ticketTypes.length) {
      fields.splice(0, 0, {
        name: "ticketTypeId",
        label: i18n.type,
        colSpan: departments && departments.length ? 6 : 12,
        isRequired: true,
        Component: Select,
        componentOpts: {
          options: ticketTypes,
          sort: "asc"
        }
      });
    }

    if (departments && departments.length) {
      fields.splice(0, 0, {
        name: "departmentId",
        label: i18n.department,
        colSpan: ticketTypes && ticketTypes.length ? 6 : 12,
        isRequired: true,
        Component: Select,
        componentOpts: {
          options: departments,
          isLoading: isLoadingDepartments,
          sort: "asc"
        }
      });
    }

    return fields;
  }, [departments, ticketTypes, departmentId, isLoadingDepartments]);

  const haveGuidelinesChanged = React.useMemo(
    () =>
      props.guidelines
        ? props.guidelines.some((x) => x.hasAccepted) && props.guidelines.some((x) => !x.hasAccepted)
        : false,
    [props.guidelines]
  );

  const additionalFields = React.useMemo(
    () =>
      !departments?.length || !!departmentId
        ? mapFormFieldsWithContextToDynamicFields(customFields?.response?.data ?? [])
        : undefined,
    [customFields, departmentId, departments]
  );

  const isVisibleVideo = React.useMemo(
    () => !!props.guidelinesVideoUrl && step === "video",
    [props.guidelinesVideoUrl, step]
  );

  const isVisibleGuidelines = React.useMemo(
    () =>
      props.displayHelpdeskGuidelines &&
      (!props.guidelinesVideoUrl || step === "guideline") &&
      !!props.guidelines?.some((x) => !x.hasAccepted),
    [props.guidelines, props.guidelinesVideoUrl, step, props.displayHelpdeskGuidelines]
  );

  const mainButton = React.useCallback(
    (p: FormModalButtonProps) => {
      if (isVisibleVideo)
        return (
          <ModuleButton
            {...getRootContainerProps(props)}
            isDisabled={props.status === "Updating"}
            onClick={() =>
              props.displayHelpdeskGuidelines && props.guidelines?.some((x) => !x.hasAccepted)
                ? setStep("guideline")
                : props.onAgree()
            }
            caption={i18n.next}
          />
        );

      if (isVisibleGuidelines)
        return (
          <ModuleButton
            {...getRootContainerProps(props)}
            isDisabled={props.status === "Updating"}
            onClick={props.onAgree}
            caption={i18n.agree}
          />
        );

      return (
        <ModuleButton
          {...getRootContainerProps(props)}
          isDisabled={props.status === "Creating" || p.isBusy}
          onClick={p.onClick}
          caption={i18n.submit}
        />
      );
    },
    [
      ...getRootContainerPropsDep(props),
      props.guidelines,
      props.onAgree,
      setStep,
      isVisibleVideo,
      isVisibleGuidelines,
      props.status
    ]
  );

  const buttons = React.useMemo(
    (): FormModalButton[] => [(p) => mainButton(p)],
    [...getRootContainerPropsDep(props), mainButton]
  );

  const violations = React.useMemo(
    () =>
      props.violations ? (
        <>
          {props.violations.map((x) => (
            <ShowMoreTextAlert
              title={x.guideline}
              description={x.description}
              color={SystemColor.Orange}
              onClose={() => props.onCloseViolation(x.guidelineViolationId)}
            />
          ))}
        </>
      ) : null,
    [props.violations, props.onCloseViolation]
  );

  const topInfo = React.useMemo(() => {
    if (isVisibleVideo) {
      return <MediaComponent sourceKey="URL" typeKey="Video" sourceId={props.guidelinesVideoUrl!} />;
    }

    if (isVisibleGuidelines) {
      return (
        <div className="guideline_container">
          {haveGuidelinesChanged && <Alert color={SystemColor.Orange}>{i18n.infoNewGuidelinesAlert}</Alert>}
          <h4>{i18n.reviewOurGuidelines}</h4>
          <div>
            {sort(props.guidelines!, { key: "hasAccepted" }).map((x) => (
              <Accordion key={x.guidelineId} autoExpand={AutoExpandItems.None} theme="tile">
                <Accordion.Item
                  label={
                    haveGuidelinesChanged && !x.hasAccepted ? (
                      <span>
                        <Badge color={SystemColor.Orange}>{i18n.newGuideline}</Badge>
                        {x.guideline}
                      </span>
                    ) : (
                      x.guideline
                    )
                  }
                >
                  <div dangerouslySetInnerHTML={{ __html: formatToHtml(x.description) }} />
                </Accordion.Item>
              </Accordion>
            ))}
          </div>
        </div>
      );
    }

    return violations;
  }, [isVisibleVideo, isVisibleGuidelines, violations, props.guidelines, props.guidelinesVideoUrl]);

  const bottomInfo = React.useMemo(
    () => <ShowMoreList className="hint" items={(props.guidelines ?? []).map((x) => x.guideline)} />,
    [props.guidelines]
  );

  const onChange = React.useCallback(
    (name: string, value: any) => {
      name === "departmentId" && setDepartmentId(value);
    },
    [setDepartmentId]
  );

  const onClose = React.useCallback(
    () => (setDepartmentId(undefined), props.onClose()),
    [props.onClose, setDepartmentId]
  );

  const onSubmit = React.useCallback(
    (data: MemberHelpdesk.View.NewTicket) =>
      props.onSubmit(
        data,
        (customFields?.response?.data ?? []).map((x) => x.formFieldId)
      ),
    [customFields, props.onSubmit]
  );

  return (
    <FormModal
      buttons={buttons}
      className="wsc_new_ticket_modal"
      error={props.error}
      fields={isVisibleVideo || isVisibleGuidelines ? [] : fields}
      Form={Form}
      formProps={getRootContainerProps(props)}
      isSubmitting={props.status === "Creating"}
      isVisible={props.isVisible}
      closeBehaviour={!isVisibleGuidelines && !isVisibleVideo ? "cross" : undefined}
      onChange={onChange}
      onClose={onClose}
      onSubmit={onSubmit}
      title={i18n.submitATicket}
      topInfo={topInfo}
      bottomInfo={isVisibleGuidelines || isVisibleVideo ? null : bottomInfo}
      uploadApiInfo={isVisibleGuidelines || isVisibleVideo ? null : props.uploadApiInfo}
      additionalFields={isVisibleVideo || isVisibleGuidelines ? [] : additionalFields}
      additionalFieldsPlacement="aboveButton"
      width="800px"
    />
  );
};
